import { ContentType } from '@creditsights/api-ui-web';
import { Button, Divider, Icon, ICONS, Tooltip, Widget } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Accordion } from 'semantic-ui-react';
import { useFetchIssuers, useFetchMultiplePageComponents } from '../../../hooks/issuers';
import useFetchIssuersByIds from '../../../hooks/issuers/useFetchIssuersByIds';
import { WIDGET_ID } from '../../../pages/Article/Article.constants';
import { PAGE_COMPONENTS } from '../../../pages/Company/Company.constants';
import { hideCSView } from '../../../pages/Company/Company.logic';
import { WeightingType } from '../../WeightIcon/WeightIcon';
import CompanyTitle from '../common/CompanyTitle';
import RecommendationDate from '../common/RecommendationDate';
import { RecommendationType } from '../common/RecommendationDate/RecommendationDate';
import { mapToRelatedCompanies, sortCompanies } from './RelatedCompaniesWidget.logic';
import styles from './RelatedCompaniesWidget.module.scss';
import {
  RelatedCompany,
  RelatedCompanyBusinessDescription,
  RelatedCompanyRecommendation,
} from './RelatedCompaniesWidget.types';

const cx = classNames.bind(styles);

interface RelatedCompaniesWidgetProps {
  focusCompanyTags: string[];
  secondaryCompanyTags: string[];
  contentType?: ContentType;
}

const renderIcon = (expanded: boolean) => {
  const label = expanded ? 'Collapse' : 'Expand';
  return (
    <Tooltip
      key={label}
      trigger={
        <Button className={cx('icon', { rotate: expanded })} ariaLabel={label}>
          <Icon width={12} height={12} name={ICONS.CaretDown} dataTestId="expand-icon" />
        </Button>
      }
      content={label}
    />
  );
};

const renderCompanyRecommendationContent = (company: RelatedCompanyRecommendation) => {
  return (
    <div className={cx('comment-container')}>
      <div className={cx('comment')} data-testid="related-companies-comment">
        {company.latestRec?.comment}
      </div>
      {company.latestRec?.weighting !== WeightingType.NoRecommendation ? (
        <div>
          <RecommendationDate
            type={RecommendationType.REVIEW}
            date={company.latestRec?.publishDate}
            articleId={company.latestRec?.publishedInArticleId}
          />
          <RecommendationDate
            type={RecommendationType.CHANGE}
            date={company.latestRec?.changedDate}
            articleId={company.latestRec?.changeMadeInArticleId}
          />
        </div>
      ) : null}
    </div>
  );
};

const renderCompanyBusinessDescriptionContent = (company: RelatedCompanyBusinessDescription) => {
  return (
    <div className={cx('comment-container')}>
      {company.pageComponent?.body && (
        <div
          className={cx('comment')}
          data-testid="related-companies-comment"
          dangerouslySetInnerHTML={{ __html: company.pageComponent.body }}
        />
      )}
    </div>
  );
};

const getInitialExpandedCompanies = (companies: RelatedCompany[]): Map<string, boolean> => {
  const expandedCompanies = new Map<string, boolean>();
  companies.forEach((company, index) => {
    expandedCompanies.set(
      company.companyTag as string,
      index === 0 &&
        !(company.kind === 'BusinessDescription' && !company.pageComponent?.body) &&
        !(company.kind === 'Recommendation' && hideCSView(company))
    );
  });
  return expandedCompanies;
};

const RelatedCompaniesWidget = ({
  focusCompanyTags,
  secondaryCompanyTags,
  contentType,
}: RelatedCompaniesWidgetProps): React.ReactElement | null => {
  const displayBusinessDescriptions = [ContentType.Covenants, ContentType.News].includes(contentType!);
  const companyTags = [...focusCompanyTags, ...secondaryCompanyTags];
  const {
    isLoading: isRecommendationsLoading,
    isError: isRecommendationsError,
    data: companies,
  } = useFetchIssuersByIds(companyTags, { enabled: !displayBusinessDescriptions });
  const { isLoading: isBusinessComponentsLoading, data: businessComponents } = useFetchMultiplePageComponents(
    companyTags,
    PAGE_COMPONENTS.COMPANY_BUSINESS_DESCRIPTION,
    {
      enabled: displayBusinessDescriptions,
    }
  );
  const {
    isLoading: isIssuersComponentsLoading,
    isError: isIssuersComponentsError,
    data: issuers,
  } = useFetchIssuers({ tags: [companyTags], onlyActive: false }, { enabled: displayBusinessDescriptions });
  const isLoading = isRecommendationsLoading || isBusinessComponentsLoading || isIssuersComponentsLoading;
  const isError = isRecommendationsError || isIssuersComponentsError;
  const relatedCompanies = useMemo(() => {
    if (!isLoading) {
      return mapToRelatedCompanies(companies, businessComponents, issuers, displayBusinessDescriptions);
    }
    return [];
  }, [companies, businessComponents, issuers, displayBusinessDescriptions, isLoading]);
  const sortedCompanies = sortCompanies(relatedCompanies, focusCompanyTags, secondaryCompanyTags);
  const [expandedCompanies, setExpandedCompanies] = useState<Map<string, boolean>>(new Map<string, boolean>());
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isEmpty(sortedCompanies) && isEmpty(expandedCompanies)) {
      setExpandedCompanies(getInitialExpandedCompanies(sortedCompanies));
    }
  }, [sortedCompanies]);

  useEffect(() => {
    if (!isLoading) {
      // Fixes an issue where on some browsers container scroll position is retained between page loads.
      // Without this expanding a section for the first time causes the container to scroll abruptly.
      containerRef.current?.scrollTo?.({ top: 0 });
    }
  }, [isLoading]);

  const handleTitleClick = (e, data) => {
    const updatedExpandedCompanies = new Map<string, boolean>(expandedCompanies);
    const isCurrentlyCollapsed = !updatedExpandedCompanies.get(data.index);
    updatedExpandedCompanies.set(data.index, isCurrentlyCollapsed);
    setExpandedCompanies(updatedExpandedCompanies);
    if (isCurrentlyCollapsed) {
      const accordionTitle = e.currentTarget;
      // Waits until container is expanded before scrolling
      setTimeout(() => {
        containerRef.current?.scrollTo?.({ top: accordionTitle.offsetTop, behavior: 'smooth' });
      }, 0);
    }
  };

  const isCompanyActive = (companyTag: string): boolean => {
    return expandedCompanies.get(companyTag) || false;
  };

  const renderCompanies = () => {
    return sortedCompanies.map((company, i) => {
      const { companyTag } = company;
      if (!companyTag) {
        return null;
      }
      const isActive = isCompanyActive(companyTag);
      const weighting = company.kind === 'Recommendation' ? company.latestRec?.weighting : undefined;
      const notClickable =
        (company.kind === 'BusinessDescription' && !company.pageComponent?.body) ||
        (company.kind === 'Recommendation' && hideCSView(company));
      return (
        <div className={cx('company-container')} key={companyTag}>
          <Accordion.Title
            onClick={notClickable ? undefined : handleTitleClick}
            active={isActive}
            index={companyTag}
            className={cx({
              'not-clickable': notClickable,
            })}
          >
            <div data-testid="related-companies-header" className={cx('header')}>
              <CompanyTitle issuerTag={companyTag} displayName={company.displayName} weighting={weighting} hasLink />
              {!notClickable && renderIcon(isActive)}
            </div>
          </Accordion.Title>
          <Accordion.Content active={isActive} data-testid={isActive ? 'expanded-content' : 'collapsed-content'}>
            {company.kind === 'Recommendation' && !hideCSView(company) && renderCompanyRecommendationContent(company)}
            {company.kind === 'BusinessDescription' && renderCompanyBusinessDescriptionContent(company)}
          </Accordion.Content>
          {sortedCompanies.length !== i + 1 && <Divider />}
        </div>
      );
    });
  };

  if (isError) {
    return null;
  }

  return (
    <div className={cx('body')} data-testid={WIDGET_ID.RELATED_COMPANIES}>
      <Widget
        headerTitle={<div className={cx('heading')}>Related Companies</div>}
        isLoading={isRecommendationsLoading}
        fixedHeight
        dataTestId="related-companies"
      >
        <div ref={containerRef} className={cx('container')} data-testid="related-companies-container">
          <Accordion>{renderCompanies()}</Accordion>
        </div>
      </Widget>
    </div>
  );
};

export default RelatedCompaniesWidget;
